import { h } from 'preact';
import { useState, useEffect, useMemo, useCallback } from 'preact/hooks';
import cls from 'clsx';
import { genUid } from '../../../uid';
import { kebabCase } from '../../../string/kebab-case';
import SimpleBar from 'simplebar';
import { isMobile } from '../../../../theme/responsive-utils';
import { debounce } from '@github/mini-throttle';
import { Button as EmbroideringButton } from '../../../../product-embroidering/button';
import { Content as EmbroideringContent } from '../../../../product-embroidering/content';
import { openModal } from '../../../../theme/modal';
import Button from '../../../../wishlist/button';
import { Content as WishlistContent } from '../../../../wishlist/content';
import { addToWishlist, removeFromWishlist, isInWishlist as checkIsInWishlist, refreshLists, createList } from '../../../../wishlist';
const optionLabels = {
    Size: 'Select a size',
    'Pant Length': 'Select pant length',
    Amount: 'Select amount'
};
const ACTIVE_CTA_QUERY_SELECTOR = '.js-product-options .js-product-options-action';
const GORGIAS_CHAT_ID = '#gorgias-chat-container';
export const ProductOptionsAsRadioButtons = function({ optionNames , optionValueGroups , focusedOptionIndex , selectedVariant , selectedOptions: initiallySelectedOptions , variants , focusOnIndex , dispatch , sale , hasProductEmbroidering , embroideringPrice , selectedVariantId , product , embroideryDisclaimer , customerId  }) {
    const [uid] = useState(genUid());
    const [isInWishlist, setIsInWishlist] = useState(false);
    const [readyWishlist, setReadyWishlist] = useState(false);
    const [actionWishlistLoading, setActionWishlistLoading] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState(initiallySelectedOptions);
    const [isStickyATCVisible, setIsStickyATCVisible] = useState(false);
    const [stickyATCExtraClass, setStickyATCExtraClass] = useState(null);
    const [stickyATCLabel, setStickyATCLabel] = useState(null);
    const [embroideryModalOpen] = useState(false);
    useEffect(()=>{
        const handleEvent = (event)=>{
            var _event_detail;
            const suggestedSize = (_event_detail = event.detail) === null || _event_detail === void 0 ? void 0 : _event_detail.size;
            if (suggestedSize) {
                console.warn('Bold Metrics SSC Flow ended. User selected size:', suggestedSize);
                setSelectedOptions((prevOptions)=>{
                    const options = [
                        ...prevOptions
                    ];
                    options[0] = suggestedSize // Set the size (first option) to the suggested size
                    ;
                    return options;
                });
            } else {
                console.warn('No suggested size found in event');
            }
        };
        window.addEventListener('bmi_add_to_cart', handleEvent);
        return ()=>{
            window.removeEventListener('bmi_add_to_cart', handleEvent);
        };
    }, []);
    const colorOptionIndex = optionNames.findIndex((name)=>name.toLowerCase() === 'color');
    const selectedColor = selectedOptions[colorOptionIndex];
    useEffect(()=>{
        document.dispatchEvent(new Event('bmi_reload'));
    }, [
        selectedColor
    ]);
    useEffect(()=>{
        Array.prototype.forEach.call(document.querySelectorAll('[data-simplebar]'), (el)=>new SimpleBar(el, {
                autoHide: false
            }));
    });
    useEffect(()=>{
        const tempWishlistItem = sessionStorage.getItem('tempWishlistProduct');
        const handleAddToWishlist = async ()=>{
            if (tempWishlistItem && window.swymCustomerId && selectedVariant) {
                const swat = window._swat;
                if (!swat) {
                    console.error('Swym API not loaded');
                    return;
                }
                const swymProduct = JSON.parse(tempWishlistItem);
                if (!window.swymSelectedListId && !window.swymSelectedListName) {
                    const list = await createList(swat, undefined, true);
                    window.swymSelectedListId = list.lid;
                    window.swymSelectedListName = list.lname;
                }
                let lid = window.swymSelectedListId;
                let name = window.swymSelectedListName;
                await addToWishlist(lid, swat, swymProduct);
                const lists = await refreshLists(swat);
                openModal(h(WishlistContent, {
                    targetEl: null,
                    selectedVariantId: selectedVariant.id,
                    productId: product.id,
                    productUrl: window.location.href,
                    variantTitle: selectedVariant.title,
                    productTitle: product.title,
                    image: (product === null || product === void 0 ? void 0 : product.featured_image) || null,
                    size: 'small',
                    isInWishlist: true,
                    action: 'added',
                    wishlists: lists,
                    product: product,
                    wishlistName: name
                }), {
                    ariaLabel: 'Wishlist form and details',
                    id: 'wishlist-modal',
                    category: 'wishlist'
                });
                setIsInWishlist(true);
                sessionStorage.removeItem('tempWishlistProduct');
            }
        };
        window.addEventListener('swym:ready', handleAddToWishlist);
        return ()=>{
            window.removeEventListener('swym:ready', handleAddToWishlist);
        };
    }, [
        product,
        selectedVariant
    ]);
    const updateWishlistStatus = useCallback(()=>{
        if (!selectedVariant) {
            setIsInWishlist(false);
            return;
        }
        setIsInWishlist(checkIsInWishlist(selectedVariant.id, product.id));
    }, [
        selectedVariant,
        product
    ]);
    const initializeWishlist = useCallback(()=>{
        if (window.swymWishLists === undefined) {
            return;
        }
        setReadyWishlist(true);
        updateWishlistStatus();
    }, [
        updateWishlistStatus
    ]);
    useEffect(()=>{
        const handleSwymReady = ()=>{
            initializeWishlist();
        };
        window.addEventListener('swym:ready', handleSwymReady);
        initializeWishlist();
        return ()=>{
            window.removeEventListener('swym:ready', handleSwymReady);
        };
    }, [
        initializeWishlist
    ]);
    useEffect(()=>{
        if (!readyWishlist) {
            return;
        }
        updateWishlistStatus();
    }, [
        readyWishlist,
        updateWishlistStatus
    ]);
    useEffect(()=>{
        if (selectedOptions.some((option)=>option !== null)) {
            sessionStorage.setItem('selectedOptions', JSON.stringify(selectedOptions));
        }
        selectedOptions.forEach((option, optionIndex)=>{
            if (option) {
                dispatch({
                    type: 'SelectOption',
                    payload: {
                        optionIndex,
                        optionValue: option
                    }
                });
            }
        });
    }, [
        selectedOptions,
        dispatch
    ]);
    useEffect(()=>{
        const activeButton = document.querySelector(ACTIVE_CTA_QUERY_SELECTOR);
        if (activeButton instanceof HTMLElement) {
            setStickyATCLabel(activeButton.innerText);
        }
    }, [
        selectedOptions
    ]);
    useEffect(()=>{
        const handleScroll = ()=>{
            const activeButton = document.querySelector(ACTIVE_CTA_QUERY_SELECTOR);
            if (activeButton instanceof HTMLElement) {
                const activeButtonOffsetTop = activeButton.offsetTop - 70 + 48 // 70 is the rough height of the header, 48 is the rough height of the cta
                ;
                const shouldStickyATCBeVisible = window.scrollY > activeButtonOffsetTop;
                setIsStickyATCVisible(shouldStickyATCBeVisible);
            }
        };
        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleScroll);
        handleScroll();
        return ()=>{
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, []);
    useEffect(()=>{
        if (isStickyATCVisible) {
            const activeButton = document.querySelector(ACTIVE_CTA_QUERY_SELECTOR);
            if (!activeButton) {
                return;
            }
            const activeButtonStyleClass = Array.from(activeButton.classList).filter((className)=>className.includes('ProductForm'));
            if (activeButtonStyleClass[0]) {
                setStickyATCExtraClass(activeButtonStyleClass[0]);
            }
        }
    }, [
        isStickyATCVisible,
        hasProductEmbroidering,
        embroideringPrice
    ]);
    // START: Hide Gorgias chat when sticky ATC is visible
    useEffect(()=>{
        const updateChatVisibility = ()=>{
            const chat = document.querySelector(GORGIAS_CHAT_ID);
            if (!chat || !(chat instanceof HTMLElement)) {
                return;
            }
            if (isMobile()) {
                chat.style.display = isStickyATCVisible ? 'none' : '';
            } else {
                chat.style.display = '';
            }
        };
        const debouncedUpdate = debounce(updateChatVisibility, 150);
        debouncedUpdate();
        const observer = new MutationObserver(()=>{
            updateChatVisibility();
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
        window.addEventListener('resize', debouncedUpdate);
        return ()=>{
            debouncedUpdate.cancel();
            observer.disconnect();
            window.removeEventListener('resize', debouncedUpdate);
        };
    }, [
        isStickyATCVisible,
        hasProductEmbroidering,
        embroideringPrice
    ]);
    // END: Hide Gorgias chat when sticky ATC is visible
    const pageTitle = document.title.toLowerCase();
    const isEgift = pageTitle.includes('e-gift');
    const isBoldMetricsActive = !!window._boldmetrics;
    const sizeLinks = useMemo(()=>[
            h('span', {
                className: 'ProductRadioButtons-link',
                role: 'button',
                tabindex: 0,
                id: 'js-size-guide'
            }, 'size guide'),
            isBoldMetricsActive && h('span', {
                className: 'ProductRadioButtons-link ProductRadioButtons-link-findMySize',
                role: 'button',
                tabindex: 0,
                id: 'boldmetrics-ssc-activator'
            }, h('span', {
                className: 'ProductRadioButtons-link-findMySize-loading'
            }, 'find my size'))
        ], [
        isBoldMetricsActive
    ]);
    const firstOptionWithoutSelectedValueIndex = optionValueGroups.findIndex((_, optionValueGroupIndex)=>{
        return selectedOptions[optionValueGroupIndex] === null;
    });
    const firstOptionWithoutSelectedValue = firstOptionWithoutSelectedValueIndex !== -1 ? optionNames[firstOptionWithoutSelectedValueIndex] : null;
    const isSelectedVariantAvailable = selectedVariant && selectedVariant.available && selectedOptions[0] === selectedVariant.option1 && selectedOptions[1] === selectedVariant.option2 && selectedOptions[2] === selectedVariant.option3;
    const isStoredSelectionValidForCurrentProduct = variants.some((variant)=>{
        return selectedOptions[0] === variant.option1 && selectedOptions[1] === variant.option2 && selectedOptions[2] === variant.option3;
    });
    // Find any variant that matches the specified option at the given index
    const findVariantWithMatchingOption = (variantList, optionValue, optionIndex)=>{
        return variantList.find((variant)=>{
            const variantOption = variant.options[optionIndex];
            return variantOption === optionValue;
        }) || null;
    };
    const handleWishlistClick = useCallback(async function() {
        setActionWishlistLoading(true);
        const swat = window._swat;
        if (!swat) {
            console.error('Swym API not loaded');
            setActionWishlistLoading(false);
            return;
        }
        if (!selectedVariant) {
            console.error('No variant selected');
            setActionWishlistLoading(false);
            return;
        }
        if (!(window === null || window === void 0 ? void 0 : window.swymCustomerId)) {
            sessionStorage.setItem('tempWishlistProduct', JSON.stringify({
                epi: selectedVariant.id,
                empi: product.id,
                du: window.location.href,
                cprops: {
                    images: product.images
                }
            }));
            window.location.href = '/account/login?return_url=' + window.location.pathname + '&variant=' + selectedVariant.id + '&add-to-wishlist=true';
            setActionWishlistLoading(false);
            return;
        }
        if (window.swymSelectedListId === undefined) {
            const list = await createList(swat, undefined, true);
            window.swymSelectedListId = list.lid;
            window.swymSelectedListName = list.lname;
        }
        const swymProduct = {
            epi: selectedVariant.id,
            empi: product.id,
            du: window.location.href,
            cprops: {
                images: product.images
            }
        };
        const newWishlistState = !isInWishlist;
        setIsInWishlist(newWishlistState);
        let lid = window.swymSelectedListId;
        let name = window.swymSelectedListName;
        if (newWishlistState) {
            await addToWishlist(lid, swat, swymProduct);
            const lists = await refreshLists(swat);
            openModal(h(WishlistContent, {
                targetEl: null,
                selectedVariantId: selectedVariant.id,
                productId: product.id,
                productUrl: window.location.href,
                variantTitle: selectedVariant.title,
                productTitle: product.title,
                image: (product === null || product === void 0 ? void 0 : product.featured_image) || null,
                size: 'small',
                wishlists: lists,
                product: product,
                isInWishlist: true,
                action: 'added',
                wishlistName: name
            }), {
                ariaLabel: 'Wishlist form and details',
                id: 'wishlist-modal',
                category: 'wishlist'
            });
        } else {
            await removeFromWishlist(lid, swat, swymProduct);
            const lists = await refreshLists(swat);
            openModal(h(WishlistContent, {
                targetEl: null,
                selectedVariantId: selectedVariant.id,
                productId: product.id,
                productUrl: window.location.href,
                variantTitle: selectedVariant.title,
                productTitle: product.title,
                image: (product === null || product === void 0 ? void 0 : product.featured_image) || null,
                size: 'small',
                wishlists: lists,
                product: product,
                isInWishlist: false,
                action: 'removed',
                wishlistName: name
            }), {
                ariaLabel: 'Wishlist form and details',
                id: 'wishlist-modal',
                category: 'wishlist'
            });
        }
        setActionWishlistLoading(false);
    }, [
        isInWishlist,
        selectedVariant,
        product
    ]);
    const renderOptionGroup = (optionGroup, optionIndex, optionName)=>{
        return optionGroup.map((optionValue)=>{
            const name = kebabCase(`${uid}-${optionName}`);
            const id = kebabCase(`${name}-${optionValue.name}`);
            const isSelected = selectedOptions.includes(optionValue.name);
            const checked = isSelected;
            const disabled = optionValue.status === 'unavailable';
            const dimmed = optionValue.status === 'unavailable' || optionValue.status === 'out-of-stock';
            const labelText = optionValue.name.trim();
            // On Sale computation — apply ONLY to the Size option
            const isSizeOption = optionName.toLowerCase() === 'size';
            let isOnSale = false;
            if (isSizeOption && sale.enabled) {
                // For size options, find any variant that has this size value on sale
                const matchingVariant = findVariantWithMatchingOption(variants, optionValue.name, optionIndex);
                if (matchingVariant) {
                    isOnSale = !!sale.variantsMap[String(matchingVariant.id)];
                }
            }
            return h('div', {
                key: id,
                className: 'ProductRadioButtons-item',
                'data-testid': 'product-option-item',
                tabindex: '0',
                onKeyUp: function(e) {
                    if (e.keyCode === 13) {
                        if (disabled) {
                            return;
                        }
                        setSelectedOptions((prevOptions)=>{
                            const options = [
                                ...prevOptions
                            ];
                            options[optionIndex] = optionValue.name;
                            return options;
                        });
                        dispatch({
                            type: 'SelectOption',
                            payload: {
                                optionIndex,
                                optionValue: optionValue.name
                            }
                        });
                    }
                }
            }, h('input', {
                id,
                name,
                checked,
                disabled,
                className: 'ProductRadioButtons-itemRadio',
                'data-testid': 'product-option-radio',
                type: 'radio',
                value: optionValue.name,
                'aria-label': `${optionName} ${optionValue.name}`,
                onFocus: function() {
                    focusOnIndex(optionIndex);
                },
                onClick: function() {
                    focusOnIndex(optionIndex);
                },
                onInput: function() {
                    if (disabled) {
                        return;
                    }
                    setSelectedOptions((prevOptions)=>{
                        const options = [
                            ...prevOptions
                        ];
                        options[optionIndex] = optionValue.name || null;
                        return options;
                    });
                    dispatch({
                        type: 'SelectOption',
                        payload: {
                            optionIndex,
                            optionValue: optionValue.name
                        }
                    });
                }
            }), h('label', {
                htmlFor: id,
                'data-testid': disabled ? 'product-option-label-disabled' : 'product-option-label',
                className: cls('ProductRadioButtons-itemLabel', {
                    'ProductRadioButtons-itemLabel--checked': checked,
                    'ProductRadioButtons-itemLabel--disabled': disabled,
                    'ProductRadioButtons-itemLabel--dimmed': dimmed
                })
            }, h('span', {
                className: 'ProductRadioButtons-itemLabelText'
            }, [
                h('span', {
                    className: 'ProductRadioButtons-itemLabelTextName'
                }, labelText),
                isOnSale && sale.suffix && h('span', {
                    className: 'ProductRadioButtons-itemLabelTextSuffix'
                }, `${sale.suffix}`)
            ])));
        });
    };
    return h('div', {
        className: 'ProductRadioButtons'
    }, optionNames.map(function(optionName, optionIndex) {
        const optionGroup = optionValueGroups[optionIndex];
        if (optionGroup == null) {
            return null;
        }
        return h('fieldset', {
            key: kebabCase(uid + optionName),
            'data-testid': 'product-option-group',
            className: cls('ProductRadioButtons-group', {
                'has-singleOption': optionGroup.length === 1
            })
        }, h('legend', {
            className: cls('ProductRadioButtons-groupTitle', {
                'ProductRadioButtons-groupTitle--focused': optionIndex === focusedOptionIndex
            })
        }, h('span', {
            className: 'ProductRadioButtons-label'
        }, `${optionName}:`), h('span', {
            className: 'ProductRadioButtons-selected'
        }, optionName.toLowerCase() === 'size' ? h('span', {
            className: 'ProductRadioButtons-size'
        }, selectedOptions[optionIndex] || '') : selectedOptions[optionIndex] || ''), optionName.toLowerCase() === 'size' && sizeLinks), h('div', {
            className: 'ProductRadioButtons-groupItems',
            'data-simplebar': ''
        }, renderOptionGroup(optionGroup, optionIndex, optionName)));
    }), hasProductEmbroidering ? firstOptionWithoutSelectedValue === null && isSelectedVariantAvailable ? h('div', {}, h(EmbroideringButton, {
        innerLabel: 'Add personalization',
        innerLabelPrice: `+${embroideringPrice}`,
        dataModalOpen: 'embroidery-modal',
        isExpanded: embroideryModalOpen,
        onModalOpen: ()=>{
            openModal(h(EmbroideringContent, {
                targetEl: null,
                selectedVariantId,
                variantTitle: selectedVariant === null || selectedVariant === void 0 ? void 0 : selectedVariant.title,
                embroideryDisclaimer
            }), {
                ariaLabel: 'Product Embroidering',
                id: 'embroidery-modal'
            });
        }
    })) : h(EmbroideringButton, {
        innerLabel: 'Add personalization',
        innerLabelPrice: `+${embroideringPrice}`,
        className: 'Product-embroideryButton Product-embroideryButton--disabled'
    }) : null, !isEgift && firstOptionWithoutSelectedValue && h('button', {
        type: 'button',
        id: 'js-select-option',
        'data-testid': 'select-option-button',
        className: cls('ProductForm-selectButton', 'o-button', 'js-product-options-action')
    }, optionLabels[firstOptionWithoutSelectedValue] || `Select ${firstOptionWithoutSelectedValue}`), !isEgift && firstOptionWithoutSelectedValue === null && isSelectedVariantAvailable && h('button', {
        id: 'js-add-to-cart',
        'data-testid': 'add-to-cart-button',
        class: cls('ProductForm-submitButton', 'o-button', 'js-product-options-action'),
        name: 'add'
    }, 'Add to Cart'), h('button', {
        id: 'notify-me-btn',
        type: 'button',
        class: cls('o-button', 'ProductForm-bts', 'js-product-options-action', {
            'is-visible': firstOptionWithoutSelectedValue === null && !isSelectedVariantAvailable && isStoredSelectionValidForCurrentProduct,
            'is-hidden': !(firstOptionWithoutSelectedValue === null && !isSelectedVariantAvailable && isStoredSelectionValidForCurrentProduct)
        })
    }, 'NOTIFY ME WHEN AVAILABLE'), firstOptionWithoutSelectedValue === null && !isSelectedVariantAvailable && !isStoredSelectionValidForCurrentProduct && h('button', {
        type: 'button',
        disabled: true,
        className: cls('ProductForm-selectButton', 'o-button')
    }, 'Not Available'), !isEgift && stickyATCLabel && h('a', {
        href: '#js-product-details-anchor',
        id: 'js-sticky-atc',
        class: cls('o-button', 'ProductForm-stickyATC', {
            'is-sticky': isStickyATCVisible
        }, stickyATCExtraClass),
        onClick: function() {
            const activeButton = document.querySelector(ACTIVE_CTA_QUERY_SELECTOR);
            if (activeButton instanceof HTMLElement) {
                activeButton.click();
            }
        }
    }, stickyATCLabel), !isEgift && readyWishlist ? [
        h(Button, {
            innerLabel: isInWishlist ? 'Added to wishlist' : 'Add to wishlist',
            className: !actionWishlistLoading && firstOptionWithoutSelectedValue === null ? 'button__wishlist' : 'button__wishlist button__wishlist--disabled',
            modalSize: 'small',
            isInWishlist: isInWishlist,
            customerId: customerId,
            onClick: handleWishlistClick
        }),
        !actionWishlistLoading && firstOptionWithoutSelectedValue !== null && h('span', {
            className: 'button__wishlist--disabled_text',
            'data-testid': 'wishlist-disabled-text'
        }, `Please select ${firstOptionWithoutSelectedValue === null || firstOptionWithoutSelectedValue === void 0 ? void 0 : firstOptionWithoutSelectedValue.toLocaleLowerCase()} to enable wishlist`)
    ] : h('button', {
        disabled: true,
        className: cls('button__wishlist', 'button__wishlist--disabled'),
        innerText: 'Loading wishlist...'
    }));
};
